import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/styles';
import { alert, success, warning } from '../../redux/ducks/notification';
import useForm from '../../hooks/use-form';
import { Container } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import Toolbar from '@material-ui/core/Toolbar';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { createIncidence } from '../../services/api/incidence-api-service';
import { createUser, getCustomers, getUsers } from '../../services/api/user-api-service';
import { getStatuses } from '../../services/api/statuses-api-service';
import { getLocations } from '../../services/api/locations-api-service';

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
  },
  isLocked: {
    cursor: 'pointer'
  },
  form: {
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(2),
    marginLeft: theme.spacing(3),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  select: {
    width: '100%',
  },
  customerData: {
    marginTop: 5,
    marginBottom: 10,
  },
  print: {
    marginTop: '18px'
  }
}));

const IncidenceCreate = ({ history }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const userId = useSelector(state => state.auth.id);

  const loading = useSelector(state => state.request.loading);
  const dispatch = useDispatch();
  const [customers, setCustomers] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [customer, setCustomer] = useState({});
  const [showCustomerForm, setShowCustomerForm] = useState(false);

  const stateSchema = {
    name: { value: '', error: '' },
    phone: { value: '', error: '' },
    technician: { value: '', error: '' },
    items: { value: '', error: '' },
    password: { value: '', error: '' },
    tasks: { value: '', error: '' },
    notes: { value: '', error: '' },
    price: { value: '', error: '' },
    serial: { value: '', error: '' },
    status: { value: '', error: '' },
    location: { value: '', error: '' },
  };

  const stateValidatorSchema = {
    name: {
      required: showCustomerForm,
      validator: {
        func: value => /^$|^([A-Za-zñÑáéíóúÁÉÍÓÚ\s]{2,20})$/.test(value),
        error: t('customer.form.validation.name'),
      },
    },
    phone: {
      required: showCustomerForm,
      validator: {
        func: value => /^$|^.{9,50}$/.test(value),
        error: t('customer.form.validation.phone'),
      },
    },
    technician: {
      required: true,
      validator: {
        func: value => /^.*/.test(value),
        error: t('incidence.form.validation.technician')
      }
    },
    items: {
      required: false,
      validator: {
        func: value => /^.*/.test(value),
        error: ''
      }
    },
    password: {
      required: false,
      validator: {
        func: value => /^.*/.test(value),
        error: ''
      }
    },
    tasks: {
      required: false,
      validator: {
        func: value => /^.*/.test(value),
        error: ''
      }
    },
    notes: {
      required: false,
      validator: {
        func: value => /^.*/.test(value),
        error: ''
      }
    },
    price: {
      required: false,
      validator: {
        func: value => /^[-+]?\d*\.?\d*$/.test(value),
        error: ''
      }
    },
    serial: {
      required: false,
      validator: {
        func: value => /^.*/.test(value),
        error: ''
      }
    },
    status: {
      required: true,
      validator: {
        func: value => /^.*/.test(value),
        error: t('incidence.form.validation.status')
      }
    },
    location: {
      required: true,
      validator: {
        func: value => /^.*/.test(value),
        error: t('incidence.form.validation.location')
      }
    },
  };

  const onSubmitForm = async () => {
    let customerId = null;
    if (showCustomerForm) {
      try {
        const response = await createUser({
          name: name,
          lastName: null,
          phone: phone,
          roles: 'ROLE_CUSTOMER',
        });
        customerId = response.data.id;
      } catch (e) {
        if (409 === e.response.status) {
          dispatch(alert(t('errors.customer.phone_exists')));
          return;
        }
        dispatch(alert(t('errors.common.server')));
      }
    } else {
      if (Object.keys(customer).length === 0) {
        dispatch(warning(t('incidence.form.validation.customer')));
        return;
      }
    }
    await postIncidence(customerId ? customerId : customer.id);
  }

  const postIncidence = async customerId => {
    try {
      await createIncidence({
        owner: userId,
        customer: customerId,
        technician: technician,
        type: 'internal',
        items: items,
        password: password,
        tasks: tasks,
        notes: notes,
        price: price ? parseFloat(price) : null,
        serial: serial,
        status: status,
        location: location,
      });
      dispatch(success(t('incidence.create.success')));
      history.push('/dashboard/incidences');
    } catch (e) {
      dispatch(alert(t('errors.common.server')));
    }
  }

  const handleCustomerChange = (event, newValue) => {
    setCustomer(newValue);
  };

  const fetchCustomers = async () => {
    try {
      const response = await getCustomers('&sort=name&order=asc&limit=10000');
      setCustomers(response.data['items']);
    } catch (e) {
      dispatch(alert(t('errors.common.server')));
    }
  }

  const fetchTechnicians = async () => {
    try {
      const response = await getUsers('&sort=name&order=asc&limit=10000&active=true');
      setTechnicians(response.data['items']);
    } catch (e) {
      dispatch(alert(t('errors.common.server')));
    }
  }

  const fetchStatuses = async () => {
    try {
      const response = await getStatuses('');
      setStatuses(response.data['items']);
    } catch (e) {
      dispatch(alert(t('errors.common.server')))
    }
  }

  const fetchLocations = async () => {
    try {
      const response = await getLocations('');
      setLocations(response.data['items']);
    } catch (e) {
      dispatch(alert(t('errors.common.server')));
    }
  }

  useEffect(() => {
    fetchCustomers();
    fetchTechnicians();
    fetchStatuses();
    fetchLocations();
  }, []); // eslint-disable-line

  const {
    dirty,
    values,
    errors,
    handleOnChange,
    handleOnSubmit,
    disable,
  } = useForm(stateSchema, stateValidatorSchema, onSubmitForm);

  const { name, phone, technician, items, password, tasks, notes, price, serial, status, location } = values;

  return (
    <div className={classes.root}>
      <Container maxWidth={'md'}>
        <Paper>
          <Toolbar>
            <Grid container>
              <Grid item md={11} xs={11}>
                <Typography variant="h5">
                  {t('incidence.toolbar.new')}
                </Typography>
              </Grid>
            </Grid>
          </Toolbar>
          <Divider />
          <div className={classes.paper}>
            <form
              className={classes.form}
              onSubmit={handleOnSubmit}
              noValidate
            >
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Button
                    variant={'outlined'}
                    size={'small'}
                    onClick={() => setShowCustomerForm(!showCustomerForm)}
                  >
                    {
                      showCustomerForm
                        ? t('button.common.new_customer_hide')
                        : t('button.common.new_customer')
                    }
                  </Button>
                </Grid>
              </Grid>
              <Grid container spacing={3} style={{ display: showCustomerForm ? '' : 'none' }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="name"
                    label={t('customer.form.fields.name')}
                    required={showCustomerForm}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    onChange={handleOnChange}
                    value={name}
                    error={errors.name.length > 0 && dirty.name}
                    helperText={errors.name}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="phone"
                    required={showCustomerForm}
                    label={t('customer.form.fields.phone')}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    onChange={handleOnChange}
                    value={phone}
                    error={errors.phone.length > 0 && dirty.phone}
                    helperText={errors.phone}
                  />
                </Grid>
              </Grid>
              {
                showCustomerForm &&
                <Fragment>
                  <Grid container>
                    <Grid item xs={12} sm={6}>
                      <Typography variant={'caption'}>{t('incidence.form.notes.customer')}</Typography>
                    </Grid>
                  </Grid>
                  <Divider style={{ marginTop: 5, marginBottom: 10 }} />
                </Fragment>
              }
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    disabled={showCustomerForm}
                    options={customers}
                    getOptionLabel={customer => customer.name && customer.lastName ? `${customer.name} ${customer.lastName}` : customer.name || customer.lastName || ''}
                    value={showCustomerForm ? '' : customer}
                    autoComplete
                    onChange={handleCustomerChange}
                    includeInputInList
                    renderInput={params => <TextField fullWidth {...params}
                                                      label={t('incidence.form.fields.customer')} />}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl required className={classes.select}>
                    <InputLabel id="user">{t('incidence.form.fields.technician')}</InputLabel>
                    <Select
                      fullWidth
                      id="technician"
                      name="technician"
                      value={technician}
                      onChange={handleOnChange}
                    >
                      {technicians.map(technician => (
                        <MenuItem key={technician.id}
                                  value={technician.id}>{technician.name} {technician.lastName}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="items"
                    label={t('incidence.form.fields.items')}
                    fullWidth
                    multiline
                    rows="5"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    onChange={handleOnChange}
                    value={items}
                    error={errors.items.length > 0 && dirty.items}
                    helperText={errors.items}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="notes"
                    label={t('incidence.form.fields.notes')}
                    fullWidth
                    multiline
                    rows="5"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    onChange={handleOnChange}
                    value={notes}
                    error={errors.notes.length > 0 && dirty.notes}
                    helperText={errors.notes}
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    name="tasks"
                    label={t('incidence.form.fields.tasks')}
                    fullWidth
                    multiline
                    rows="5"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    onChange={handleOnChange}
                    value={tasks}
                    error={errors.tasks.length > 0 && dirty.tasks}
                    helperText={errors.tasks}
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    name="password"
                    label={t('incidence.form.fields.password')}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    onChange={handleOnChange}
                    value={password}
                    error={errors.password.length > 0 && dirty.password}
                    helperText={errors.password}
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    name="serial"
                    label={t('incidence.form.fields.serial')}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    onChange={handleOnChange}
                    value={serial}
                    error={errors.serial.length > 0 && dirty.serial}
                    helperText={errors.serial}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl className={classes.select}>
                    <InputLabel id="status">{t('incidence.form.fields.status')}</InputLabel>
                    <Select
                      fullWidth
                      id="status"
                      name="status"
                      value={status}
                      onChange={handleOnChange}
                    >
                      {statuses.map(status => (
                        <MenuItem key={status.id} value={status.id}>{t(`status.${status.name}`)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl className={classes.select}>
                    <InputLabel id="location">{t('incidence.form.fields.location')}</InputLabel>
                    <Select
                      fullWidth
                      id="location"
                      name="location"
                      value={location}
                      onChange={handleOnChange}
                    >
                      {locations.map(location => (
                        <MenuItem key={location.id} value={location.id}>{t(`location.${location.name}`)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={6}>
                  <TextField
                    name="price"
                    type="number"
                    label={t('incidence.form.fields.price')}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    onChange={handleOnChange}
                    value={price}
                    error={errors.price.length > 0 && dirty.price}
                    helperText={errors.price}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} sm={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  className={classes.submit}
                  disabled={disable || loading}
                >
                  {t('incidence.form.fields.button.save')}
                </Button>
              </Grid>
            </form>
          </div>
        </Paper>
      </Container>
    </div>
  );
};

export default IncidenceCreate;
